import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import api from 'api';
import {
  DEAL_ROOM_SEARCH,
  DealRoomSearch,
  DealRoomSearchResults,
} from 'models/Search';
import useQueryParams from 'hooks/useQueryParams';
import AuthContext from 'providers/Auth/Auth.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DealRoomCard } from './components/DealRoomCard';
import { Loader } from 'ncoded-component-library';
import { ALL_DEALS, getSearchSectionTitle, isValidDealSection } from './utils';
import { SearchFilters } from './components/SearchFilters';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { SearchInitiatedEventProps } from 'models/MixpanelEvents';

import './Search.styles.scss';

const EMPTY_STATE: DealRoomSearchResults = {
  drafts: [],
  selling: [],
  buying: [],
  marketplace: [],
};

const SearchPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState(ALL_DEALS);
  const [searchResults, setSearchResults] =
    useState<DealRoomSearchResults>(EMPTY_STATE);

  const { popServerError } = useContext(PopNotificationsContext);
  const { isLoggedIn } = useContext(AuthContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);

  const {
    params: { searchString },
  } = useQueryParams();
  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const trimmedSearchString = useMemo(
    () => (searchString || '').trim().replaceAll(/\s+/g, ' '),
    [searchString],
  );

  const displayedDeals = useMemo(
    () =>
      Object.entries(searchResults).filter(([key, value]) =>
        isValidDealSection(key, value.length, filter, isLoggedIn, isSuperAdmin),
      ),
    [filter, isLoggedIn, isSuperAdmin, searchResults],
  );

  const filters = useMemo(() => {
    const visibleFilters = {
      ...(!!searchResults[DEAL_ROOM_SEARCH.DRAFTS].length && {
        [DEAL_ROOM_SEARCH.DRAFTS]:
          searchResults[DEAL_ROOM_SEARCH.DRAFTS].length,
      }),
      [DEAL_ROOM_SEARCH.SELLING]:
        searchResults[DEAL_ROOM_SEARCH.SELLING].length,
      ...(!isSuperAdmin && {
        [DEAL_ROOM_SEARCH.BUYING]:
          searchResults[DEAL_ROOM_SEARCH.BUYING].length,
      }),
      [DEAL_ROOM_SEARCH.MARKETPLACE]:
        searchResults[DEAL_ROOM_SEARCH.MARKETPLACE].length,
    };

    return {
      [ALL_DEALS]: Object.values(visibleFilters).reduce(
        (acc, curr) => acc + curr,
        0,
      ),
      ...visibleFilters,
    };
  }, [isSuperAdmin, searchResults]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        mixpanelService.searchInitiated(
          getMixpanelProps({
            searchedTerm: trimmedSearchString,
          }) as SearchInitiatedEventProps,
        );

        const { data } = await api.general[
          isLoggedIn ? 'searchDeals' : 'searchMarketPlace'
        ](trimmedSearchString);

        setSearchResults(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isLoggedIn, trimmedSearchString, popServerError, getMixpanelProps]);

  return (
    <div className="search-page">
      <h1>{t('searchResults', { searchString: trimmedSearchString })}</h1>

      {isLoggedIn && (
        <SearchFilters
          filters={filters}
          activeFilter={filter}
          setFilter={setFilter}
        />
      )}

      {displayedDeals.map(([type, value]) => (
        <React.Fragment key={type}>
          <h3>
            {t(getSearchSectionTitle(type, isSuperAdmin))}
            {` (${value.length})`}
          </h3>
          <div className={classNames('search-page__section', 'white-panel')}>
            {isLoading ? (
              <div>
                <Loader />
              </div>
            ) : value.length ? (
              value.map((deal) => (
                <DealRoomCard
                  key={deal._id}
                  dealRoom={deal}
                  searchString={trimmedSearchString}
                  type={type as DealRoomSearch}
                />
              ))
            ) : (
              <div>
                {t('foundNoDealsWhichMention', {
                  searchString: trimmedSearchString,
                })}
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default SearchPage;
